import axios from 'axios';
import { useEffect, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { HTTP_STATUS } from './constants/httpStatus.constant';
import { reload, isNativePlatform } from './utils/device.util';
import MaintenancePage from './components/common/MaintenancePage';
import Cookie from './utils/cookie.util';

const Maintenance = ({ children }) => {
    const [isNative, setIsNative] = useState(null);
    const [isMaintenance, setIsMaintenance] = useState(false);

    // ✅ Detect platform
    useEffect(() => {
        isNativePlatform().then(setIsNative);
    }, []);

    // ✅ Health check function (memoized)
    const checkHealth = async () => {
        try {
            if (import.meta.env.APP_ENV === 'development') return;
            if (isNative === null) return;

            const BASE_URL = isNative ? import.meta.env.APP_CONFIG_SERVER_PROXY : '/api';
            const { status } = await axios.get(`${BASE_URL}/common/health?ts=${Date.now()}`);

            handleMaintenance([HTTP_STATUS.MAINTENANCE, HTTP_STATUS.BAD_GATEWAY, undefined].includes(status));
        } catch (error) {
            const isNowMaintenance =
                [HTTP_STATUS.MAINTENANCE, HTTP_STATUS.BAD_GATEWAY, undefined].includes(error.response?.status);

            handleMaintenance(isNowMaintenance);
        }
    }

    // ✅ Main effect
    useEffect(() => {
        if (import.meta.env.APP_ENV === 'development') return;
        if (isNative === null) return;

        // 🔥 initial check
        checkHealth();

        // 🔁 polling
        const interval = setInterval(checkHealth, 30000);

        // 📱 Capacitor resume
        let sub;
        const setupListener = async () => {
            if (isNative) {
                sub = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
                    if (isActive) {
                        checkHealth();
                    }
                });
            }
        };

        setupListener();

        return () => {
            clearInterval(interval);
            if (sub) sub.remove();
        };
    }, [isNative]);

    // ✅ Maintenance handler
    const handleMaintenance = async (isNowMaintenance) => {
        const wasReloaded = await Cookie.get('maintenanceReloaded');

        // 🔁 Reload only once
        if (isNowMaintenance && !isNative && !wasReloaded) {
            await Cookie.set('maintenanceReloaded', 'true', 1); // expire in 1 day
            reload();
            return;
        }

        // ✅ Reset when server is back
        if (!isNowMaintenance && wasReloaded) {
            await Cookie.erase('maintenanceReloaded');
        }

        setIsMaintenance(isNowMaintenance);
    };

    // ⏳ Prevent flicker
    if (isNative === null) return null;

    return (
        (isMaintenance && !isNative) ? <MaintenancePage isNative={isNative} /> : children
    );

};

export default Maintenance;